// EXAMS-SEND-2026-10-16 §2: the one Send dialog for a link just made -- an exam link or an onboarding link. Three options,
// any combination, none required: Copy link, Text (the agents window's route), Email (from Matt through crm-mailer;
// EMAIL-CRM-2026-10-22: never a mail app -- if the company email is down, Email is off and says so). The message is
// prefilled and editable; text and email carry the same words. Each send is logged on the candidate's timeline
// (lib/sendLink.js; an email also as "Email · <subject> · Sent"/"Bounced"). Someone marked No email gets no Email button.
import { useEffect, useRef, useState } from 'react';
import { messageFor } from './lib/sendLink';
import TemplatePicker from './TemplatePicker';                         // NIGHT-DIRECTSTAFF-2026-10-25 §3
import { unresolved } from './lib/templates';
import { NO_EMAIL_WHY, FROM_LINE } from './lib/email';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
  border: 0, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' });
const note = { fontSize: 12, color: '#475569', margin: '6px 0 0' };
const digits = (v) => String(v || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

/**
 * kind 'exam' | 'onboarding';  link: the URL (shown once);  linkRef: the exam link id or the onboarding session id
 * candidate { id, name, phone, email };  api: makeSendApi(...);  by: who is sending (for the timeline)
 * initialBody: a different prefilled message (the exam reminder, NIGHT-DIRECTSTAFF-2026-10-17 §3)
 * primary 'text' | 'email' (NIGHT-DIRECTSTAFF-2026-10-21 §5): which comes first -- 'text' = Text, Email, Copy; 'email' = Email,
 *   Copy, Text. textConsent false = Text off with the reason (the caller knows they didn't tick the box). Omitted: as before.
 * link '': a message with no link (an exam result, NIGHT-DIRECTSTAFF-2026-10-19 §3) -- Copy copies the message, and Email sends it
 *   from Matt as a plain message (kind 'message', EMAIL-CRM-2026-10-22).
 */
// templateCtx (10-25 §3): the approved templates this box can offer (lib/templates.js); null = no picker (as before)
export default function SendLink({ kind, link, linkRef, candidate, api, by = '', toast = () => {}, onSent = () => {}, initialBody = '', primary = null, textConsent = null, templateCtx = null }) {
  const [body, setBody] = useState(() => initialBody || messageFor(kind, candidate && candidate.name, link));
  const [emailReady, setEmailReady] = useState(null);           // null = checking
  const [text, setText] = useState({ busy: false, sent: false, msg: '', code: '' });
  const [mail, setMail] = useState({ busy: false, sent: false, msg: '' });
  const [anyway, setAnyway] = useState(false);
  const [optedOut, setOptedOut] = useState(false);
  const inFlight = useRef(false);                                // one send per click, whatever the network does
  useEffect(() => { let live = true; api.emailReady().then((r) => { if (live) setEmailReady(r); }); return () => { live = false; }; }, [api]);
  useEffect(() => {
    let live = true;
    if (api.optedOut) api.optedOut(candidate).then((r) => { if (live) setOptedOut(r === true); }).catch(() => {});
    return () => { live = false; };
  }, [api, candidate]);

  const hasPhone = digits(candidate && candidate.phone).length === 10;
  const email = String((candidate && candidate.email) || '').trim();
  const keepsLink = (!link || body.includes(link)) && unresolved(body).length === 0;   // 10-25 §3: nor an unfilled placeholder
  const logged = async (channel) => {
    try { await api.log(candidate.id, kind, linkRef, channel, by); } catch (e) { toast(e.message); }
    onSent(channel);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link || body); toast(link ? 'Link copied' : 'Message copied'); } catch { toast('Copy failed — select it and copy it'); }
  };
  const sendText = async () => {
    if (inFlight.current || text.sent) return;
    inFlight.current = true;
    setText({ busy: true, sent: false, msg: '', code: '' });
    try {
      const r = await api.text(candidate, body, anyway);
      setText({ busy: false, sent: true, msg: r && r.duplicate ? 'Already sent a moment ago; not sent twice.' : 'Texted.', code: '' });
      if (!(r && r.duplicate)) await logged('text');
    } catch (e) {
      setText({ busy: false, sent: false, msg: e.message || 'The text was not sent.', code: (e && e.code) || '' });
    } finally { inFlight.current = false; }
  };
  const sendEmail = async () => {
    if (inFlight.current || mail.sent) return;
    inFlight.current = true;
    setMail({ busy: true, sent: false, msg: '' });
    try {
      await api.email(kind, email, body, candidate && candidate.id);
      setMail({ busy: false, sent: true, msg: `Emailed to ${email}.` });
      await logged('email');
    } catch (e) {
      if (e && e.code === 'email_not_ready') setEmailReady(false);   // the company email went away: Email off, said
      if (e && e.code === 'recipient_suppressed') setOptedOut(true);  // a bounce or complaint: No email from now on
      setMail({ busy: false, sent: false, msg: e.message || 'The email was not sent.' });
    } finally { inFlight.current = false; }
  };

  const textOff = !hasPhone || !keepsLink || text.busy || text.sent || textConsent === false;
  const mailOff = !email || !keepsLink || mail.busy || mail.sent || optedOut || emailReady !== true;
  const actions = {
    copy: <button className="nb" data-testid="send-link-copy" onClick={copy} style={btn('#f1f5f9', '#334155')}>{link ? 'Copy link' : 'Copy message'}</button>,
    text: <button className="nb" data-testid="send-link-text" onClick={sendText} disabled={textOff} title={!hasPhone ? 'No mobile number on file' : textConsent === false ? 'No text consent on file' : undefined}
      style={btn('#0e7490', 'white', textOff)}>{text.sent ? 'Texted ✓' : text.busy ? 'Sending…' : 'Text'}</button>,
    email: (
      <button className="nb" data-testid="send-link-email" onClick={sendEmail} disabled={mailOff}
        title={!email ? 'No email on file' : optedOut ? 'No email' : emailReady === false ? 'The company email is not available right now' : undefined}
        style={btn('#1d4ed8', 'white', mailOff)}>
        {mail.sent ? 'Emailed ✓' : mail.busy ? 'Sending…' : optedOut ? 'No email' : emailReady === null ? 'Email…' : 'Email'}</button>
    ),
  };
  const order = primary === 'text' ? ['text', 'email', 'copy'] : primary === 'email' ? ['email', 'copy', 'text'] : ['copy', 'text', 'email'];
  return (
    <div data-testid="send-link" style={{ marginTop: 16, background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, padding: 12 }}>
      <p style={{ fontSize: 12, color: '#0e7490', margin: '0 0 6px' }}>{link ? 'Shown once. Send it now: copy it, text it, email it, or any of them.' : 'Send it: copy it, text it, email it, or any of them.'}</p>
      {link && <code data-testid="send-link-url" style={{ wordBreak: 'break-all', fontSize: 13 }}>{link}</code>}
      {templateCtx && <TemplatePicker ctx={{ name: candidate && candidate.name, kind, link, ...templateCtx }} channel="send" preview={body} onPick={(t) => setBody(t.body)} />}
      <label htmlFor="send-link-body" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginTop: 10 }}>Message (the text and the email say the same)</label>
      <textarea id="send-link-body" data-testid="send-link-body" className="input" rows={4} value={body} onChange={(e) => setBody(e.target.value)}
        style={{ marginTop: 4, fontSize: 13 }} />
      {link && !body.includes(link) && <p data-testid="send-link-no-link" style={{ ...note, color: '#b91c1c' }}>The message no longer has the link. Put it back to text or email it.</p>}
      {/* §5 (10-21): the buttons in the order the caller asks for -- Text first when they consented */}
      <div data-testid="send-link-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        {order.map((k) => <span key={k} style={{ display: 'contents' }}>{actions[k]}</span>)}
      </div>
      {textConsent === false && <p data-testid="send-link-no-consent" style={note}>No text consent on file, so Text is off. Email them instead.</p>}
      {!hasPhone && <p data-testid="send-link-no-phone" style={note}>No mobile number on file, so Text is off.</p>}
      {!email && <p data-testid="send-link-no-email" style={note}>No email on file, so Email is off.</p>}
      {email && optedOut && <p data-testid="send-link-opted-out" style={{ ...note, color: '#b91c1c' }}>{NO_EMAIL_WHY}</p>}
      {email && !optedOut && emailReady === false && <p data-testid="send-link-email-note" style={note}>
        The company email is not available right now, so Email is off. Copy or text it, or try again in a few minutes.</p>}
      {email && !optedOut && emailReady === true && <p data-testid="send-link-from" style={note}>Email: {FROM_LINE}</p>}
      {text.code === 'quiet_hours' && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44, fontSize: 13 }}>
          <input type="checkbox" data-testid="send-link-anyway" checked={anyway} onChange={(e) => setAnyway(e.target.checked)} style={{ width: 18, height: 18 }} />
          Send anyway (it's outside 8am to 8pm their time)
        </label>
      )}
      {text.msg && <p data-testid="send-link-text-status" role="status" style={{ ...note, color: text.sent ? '#15803d' : '#b91c1c' }}>{text.msg}</p>}
      {mail.msg && <p data-testid="send-link-email-status" role="status" style={{ ...note, color: mail.sent ? '#15803d' : '#b91c1c' }}>{mail.msg}</p>}
    </div>
  );
}
